import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightOnRectangleIcon, PhotoIcon, CloudArrowUpIcon, CheckCircleIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const feedbackVariants = {
  success: 'text-green-700 bg-green-50 border-green-100',
  error: 'text-red-700 bg-red-50 border-red-100',
  info: 'text-blue-700 bg-blue-50 border-blue-100'
};

const initialPasswordState = {
  old_password: '',
  new_password: '',
  re_new_password: ''
};

const StudentSettings = ({ onLogout }) => {
  const { userProfile, refreshProfile, avatarUrl, updateAvatar } = useAuth();
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: ''
  });
  const [profileFeedback, setProfileFeedback] = useState(null);
  const [passwordForm, setPasswordForm] = useState(initialPasswordState);
  const [passwordFeedback, setPasswordFeedback] = useState(null);
  const [avatarFeedback, setAvatarFeedback] = useState(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(avatarUrl);
  const [passwordVisibility, setPasswordVisibility] = useState({
    old_password: false,
    new_password: false,
    re_new_password: false
  });

  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        phone: userProfile.phone || ''
      });
    }
  }, [userProfile]);

  useEffect(() => {
    setAvatarPreview(avatarUrl || null);
  }, [avatarUrl]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    setProfileFeedback(null);
    setIsUpdatingProfile(true);

    try {
      const payload = {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        phone: profileForm.phone,
      };

      await apiService.updateUserProfile(payload);
      await refreshProfile();

      setProfileFeedback({
        type: 'success',
        message: 'Profile updated successfully.'
      });
    } catch (error) {
      setProfileFeedback({
        type: 'error',
        message: error.message || 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordFeedback(null);
    setIsChangingPassword(true);

    try {
      await apiService.changePassword(passwordForm);
      setPasswordFeedback({
        type: 'success',
        message: 'Password changed successfully.'
      });
      setPasswordForm(initialPasswordState);
    } catch (error) {
      setPasswordFeedback({
        type: 'error',
        message: error.message || 'Failed to change password. Please verify your details.'
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setAvatarFeedback({
        type: 'error',
        message: 'Please select a valid image file.'
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setAvatarFeedback({
        type: 'error',
        message: 'Image size should not exceed 2MB.'
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        updateAvatar(result);
        setAvatarPreview(result);
        setAvatarFeedback({
          type: 'success',
          message: 'Profile picture updated.'
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleAvatarRemove = () => {
    updateAvatar(null);
    setAvatarPreview(null);
    setAvatarFeedback({
      type: 'info',
      message: 'Profile picture removed.'
    });
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const renderFeedback = (feedback) => {
    if (!feedback) {
      return null;
    }

    const Icon = feedback.type === 'success' ? CheckCircleIcon : feedback.type === 'error' ? ExclamationTriangleIcon : PhotoIcon;
    const classes = feedbackVariants[feedback.type] || feedbackVariants.info;

    return (
      <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm ${classes}`}>
        <Icon className="w-5 h-5" />
        <span>{feedback.message}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <header className="glass-effect border-b border-white/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600">
                <ArrowLeftIcon className="w-5 h-5 mr-1" />
                Back to Dashboard
              </Link>
              <span className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                Student Settings
              </span>
            </div>

            <button
              onClick={onLogout}
              className="p-2 text-gray-600 hover:text-red-600 transition-colors"
              title="Logout"
            >
              <ArrowRightOnRectangleIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <div className="flex justify-center">
          <section className="card w-full lg:w-1/2">
            <div className="flex flex-col items-center gap-4 mb-6">
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 flex items-center justify-center text-white overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Profile preview" className="w-full h-full object-cover" />
                ) : (
                  <PhotoIcon className="w-10 h-10" />
                )}
              </div>
              <div className="text-center">
                <h2 className="text-xl font-semibold text-gray-900">Profile Picture</h2>
                <p className="text-sm text-gray-500">Upload a clear photo so classmates and coaches recognise you easily.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <label className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg shadow-sm hover:bg-primary-700 cursor-pointer transition">
                  <CloudArrowUpIcon className="w-5 h-5" />
                  Upload Photo
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
                </label>
                {avatarPreview && (
                  <button
                    type="button"
                    onClick={handleAvatarRemove}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            {renderFeedback(avatarFeedback)}
          </section>
        </div>

        <div className="flex justify-center">
          <section className="card w-full lg:w-1/2">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
              <p className="text-sm text-gray-500">Keep your contact details up to date for notifications and certificates.</p>
            </div>

            <form className="space-y-6" onSubmit={handleProfileSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="first_name">First Name</label>
                <input
                  id="first_name"
                  name="first_name"
                  value={profileForm.first_name}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your first name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="last_name">Last Name</label>
                <input
                  id="last_name"
                  name="last_name"
                  value={profileForm.last_name}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  name="phone"
                  value={profileForm.phone}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter your phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="email">Email</label>
                <input
                  id="email"
                  value={userProfile?.email || ''}
                  disabled
                  className="w-full px-4 py-3 border border-gray-100 rounded-xl bg-gray-50 text-gray-500"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                {renderFeedback(profileFeedback)}
                <button
                  type="submit"
                  className={`px-6 py-3 rounded-xl text-sm font-semibold text-white transition ${isUpdatingProfile ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
                  disabled={isUpdatingProfile}
                >
                  {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </section>
        </div>

        <div className="flex justify-center">
          <section className="card w-full lg:w-1/2">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Security</h2>
              <p className="text-sm text-gray-500">Change your password regularly to keep your account secure.</p>
            </div>

            <form className="space-y-6" onSubmit={handlePasswordSubmit}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="old_password">Current Password</label>
                <div className="relative">
                  <input
                    id="old_password"
                    name="old_password"
                    type={passwordVisibility.old_password ? 'text' : 'password'}
                    value={passwordForm.old_password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter your current password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('old_password')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={passwordVisibility.old_password ? 'Hide current password' : 'Show current password'}
                  >
                    {passwordVisibility.old_password ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="new_password">New Password</label>
                <div className="relative">
                  <input
                    id="new_password"
                    name="new_password"
                    type={passwordVisibility.new_password ? 'text' : 'password'}
                    value={passwordForm.new_password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Create a new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new_password')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={passwordVisibility.new_password ? 'Hide new password' : 'Show new password'}
                  >
                    {passwordVisibility.new_password ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left" htmlFor="re_new_password">Confirm New Password</label>
                <div className="relative">
                  <input
                    id="re_new_password"
                    name="re_new_password"
                    type={passwordVisibility.re_new_password ? 'text' : 'password'}
                    value={passwordForm.re_new_password}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Confirm your new password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('re_new_password')}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                    aria-label={passwordVisibility.re_new_password ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {passwordVisibility.re_new_password ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                {renderFeedback(passwordFeedback)}
                <button
                  type="submit"
                  className={`px-6 py-3 rounded-xl text-sm font-semibold text-white transition ${isChangingPassword ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}
                  disabled={isChangingPassword}
                >
                  {isChangingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
};

export default StudentSettings;

